import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'

export const EMPTY_PASSPORT_CARD_TRAVELLER_ADDRESS: App.PassportCardTravellerAddress = {
  country: 'AU',
  state: '',
  street: '',
  city: '',
  postcode: '',
}

export const PASSPORT_CARD_MAX_START_DATE_IN_DAYS = 365
export const PASSPORT_CARD_TRAVELLER_NAME_PATTERN = "^(?=.*[^\\s])[A-Za-zÀ-ÖØ-öø-ÿ ł,\\-'\\.]+$"

export function isPassportCardStartDateWithinAllowedRange(
  startDate?: string,
  referenceDate = moment(),
): boolean {
  if (!startDate) return false

  const currentDay = moment(referenceDate).startOf('day')
  const tripStartDay = moment(startDate).startOf('day')
  const daysUntilTripStart = tripStartDay.diff(currentDay, 'days')

  return daysUntilTripStart >= 0 && daysUntilTripStart <= PASSPORT_CARD_MAX_START_DATE_IN_DAYS
}

export function getPassportCardMaxStartDate(referenceDate = moment()): string {
  return moment(referenceDate)
    .startOf('day')
    .add(PASSPORT_CARD_MAX_START_DATE_IN_DAYS, 'days')
    .format(ISO_DATE_FORMAT)
}

export function travellersFromOccupants(occupants: App.Occupants): Array<App.PassportCardTravellerDetail> {
  const adults = Array.from({ length: occupants.adults || 0 }, () => ({ type: 'adult' as const, dateOfBirth: '' }))
  const childrenCount = (occupants.children || 0) + (occupants.infants || 0)
  const children = Array.from({ length: childrenCount }, () => ({ type: 'child' as const, dateOfBirth: '' }))
  return [...adults, ...children]
}

export function getPassportCardPricePerPerson(price: number, travellerCount: number): number {
  return travellerCount > 0 ? price / travellerCount : price
}

function addActiveOptionalBenefitPrices(
  price: number,
  optionalBenefits: Array<App.PassportCardOptionalBenefit>,
  activeOptionalBenefitCodes: Array<string>,
): number {
  let total = price

  for (const benefit of optionalBenefits) {
    if (activeOptionalBenefitCodes.includes(benefit.code)) {
      total += benefit.price.consumerPrice
    }
  }

  return total
}

export interface PassportCardProposalTotals {
  totalPrice: number
  loyaltyTotalPrice?: number
}

/**
 * Whole-party price of a PassportCard proposal including its active optional covers. The
 * loyalty total is only set when the customer can redeem the quote's loyalty price.
 */
export function getPassportCardProposalTotals(
  proposal: App.PassportCardProposal,
  activeOptionalBenefitCodes: Array<string>,
  canRedeemLoyaltyPrice: boolean,
): PassportCardProposalTotals {
  const totalPrice = addActiveOptionalBenefitPrices(
    proposal.price.consumerPrice,
    proposal.optionalBenefits,
    activeOptionalBenefitCodes,
  )

  if (!canRedeemLoyaltyPrice || proposal.loyaltyPrice == null) {
    return { totalPrice }
  }

  return {
    totalPrice,
    loyaltyTotalPrice: addActiveOptionalBenefitPrices(
      proposal.loyaltyPrice,
      proposal.optionalBenefits,
      activeOptionalBenefitCodes,
    ),
  }
}

export function formatOptionalCoversTitle(title: string): string {
  return title.replace('\nShow optional covers', '').trim()
}

export function occupantsFromTravellers(travellers: Array<App.PassportCardTravellerDetail>): App.Occupants {
  return travellers.reduce<App.Occupants>((result, traveller) => {
    if (traveller.type === 'adult') {
      return {
        ...result,
        adults: (result.adults || 0) + 1,
      }
    }
    return {
      ...result,
      children: (result.children || 0) + 1,
    }
  }, { adults: 0, children: 0, infants: 0 })
}

export function travellerTypeFromDob(dateOfBirth: string): App.PassportCardTravellerType {
  const age = moment().diff(moment(dateOfBirth, 'YYYY-MM-DD', true), 'years')
  return age >= 18 ? 'adult' : 'child'
}

export function travellersFromTravellerDetails(
  travellersDetails: Array<App.PassportCardTravellerDetail>,
): Array<App.PassportCardInsuranceTraveller> {
  return travellersDetails.map(traveller => ({
    firstName: '',
    lastName: '',
    ageRange: traveller.type,
    dateOfBirth: traveller.dateOfBirth || '',
  }))
}

export function travellerDetailsFromInsuranceTravellers(
  travellers: Array<App.PassportCardInsuranceTraveller>,
): Array<App.PassportCardTravellerDetail> {
  return travellers.map(traveller => ({
    type: traveller.ageRange,
    dateOfBirth: traveller.dateOfBirth || '',
  }))
}

/**
 * Whether the traveller identity fields sourced from the vertical traveller forms are
 * present. The policy holder additionally needs contact details for policy issuance.
 */
export function isPassportCardTravellerVerticalDataComplete(
  traveller: App.PassportCardInsuranceTraveller,
  isPolicyHolder: boolean,
): boolean {
  return !!traveller.firstName &&
    !!traveller.lastName &&
    !!traveller.dateOfBirth &&
    (!isPolicyHolder || (!!traveller.email && !!traveller.phonePrefix && !!traveller.phone))
}

/** Whether the policy holder residential address has every field PassportCard requires. */
export function isPassportCardTravellerAddressComplete(
  address: App.PassportCardTravellerAddress | undefined,
): address is App.PassportCardTravellerAddress {
  return !!address?.country &&
    !!address.state &&
    !!address.street &&
    !!address.city &&
    !!address.postcode
}

export function mergeTravellerDetailsIntoInsuranceTravellers(
  travellersDetails: Array<App.PassportCardTravellerDetail>,
  existingTravellers: Array<App.PassportCardInsuranceTraveller> = [],
): Array<App.PassportCardInsuranceTraveller> {
  return travellersDetails.map((travellerDetail, index) => {
    const existingTraveller = existingTravellers[index]

    return {
      firstName: existingTraveller?.firstName || '',
      lastName: existingTraveller?.lastName || '',
      // Conditional so a titleless traveller round-trips deep-equal, not with an undefined key.
      ...(existingTraveller?.title ? { title: existingTraveller.title } : {}),
      ageRange: travellerDetail.type,
      dateOfBirth: travellerDetail.dateOfBirth || '',
      ...(index === 0 ? { address: existingTraveller?.address || { ...EMPTY_PASSPORT_CARD_TRAVELLER_ADDRESS } } : {}),
      email: existingTraveller?.email,
      phonePrefix: existingTraveller?.phonePrefix,
      phone: existingTraveller?.phone,
    }
  })
}
